import React from 'react';
import PropTypes from 'prop-types';
import useSwipe from '../hooks/useSwipe';
import useDoubleClick from '../hooks/useDoubleClick';
import { roundDistance } from '../utils/formatters';
import { REST_DAY_ICONS } from '../constants';

const RunCard = ({
  day,
  run,
  isCompleted,
  isToday,
  darkMode,
  theme,
  verticalLayout,
  showRunEmojis,
  c25kViewMode,
  getRunEmoji,
  toggleComplete,
  triggerHaptic,
  setSelectedDate,
  setShowAddRun,
  setEditingRun,
  restDayIcon,
  isReorderMode = false,
  isDragging = false,
  isDropTarget = false,
  onDragStart,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDragEnd,
  onDrop,
  onTouchDragStart,
  onTouchDragEnd,
  onTouchCancel
}) => {
  // Get themed border color
  const themeBorder = theme.primary.replace('bg-', 'border-');

  // Get theme colors for completed state
  const themeColor = theme.primary.replace('bg-', ''); // e.g., 'sky-500'
  const themeCompletedBorder = `border-${themeColor}`;
  const themeCompletedBg = darkMode ? theme.primary.replace('bg-', 'bg-').replace('-500', '-950').replace('-600', '-950').replace('-700', '-950') : theme.light;
  const themeCompletedText = darkMode ? theme.text.replace('text-', 'text-').replace('-500', '-400').replace('-600', '-400').replace('-700', '-400') : theme.text;

  const isRest = run && run.status === 'rest';
  const restIconConfig = REST_DAY_ICONS[restDayIcon] || REST_DAY_ICONS.sleep;

  // Auto-complete rest days on/after their date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const runDate = new Date(day);
  runDate.setHours(0, 0, 0, 0);
  const isRestCompleted = isRest && runDate <= today;

  // Treat rest days as completed if date has passed, otherwise as completed runs
  const showAsCompleted = isCompleted || isRestCompleted;

  // Build the border class
  const getBorderClass = () => {
    if (isDragging) return darkMode ? 'border-blue-500 border-dashed' : 'border-blue-400 border-dashed';
    if (isDropTarget && !isDragging) return darkMode ? 'border-purple-500 border-[3px]' : 'border-purple-400 border-[3px]';
    if (showAsCompleted) return themeCompletedBorder;
    if (isToday) return themeBorder + ' border-[3px]';
    if (run) return darkMode ? 'border-slate-600' : 'border-slate-300';
    return darkMode ? 'border-slate-700 border-dashed' : 'border-slate-200 border-dashed';
  };

  // Build the background class
  const getBackgroundClass = () => {
    if (isDropTarget && !isDragging) return darkMode ? 'bg-purple-950 ' : 'bg-purple-50 ';
    if (showAsCompleted) return themeCompletedBg + ' ';
    if (run) return darkMode ? 'bg-slate-800 ' : 'bg-white shadow-sm ';
    return darkMode ? 'bg-slate-900 ' : 'bg-slate-50 ';
  };

  // Swipe handlers for runs - disabled in reorder mode
  const swipeHandlers = useSwipe(
    // Swipe left - edit/delete
    () => {
      if (!run || isReorderMode) return; // Disable in reorder mode
      triggerHaptic(30);
      setSelectedDate(day);
      setEditingRun(run);
      setShowAddRun(true);
    },
    // Swipe right - toggle complete (skip rest days)
    () => {
      if (!run || isRest || isReorderMode) return; // Disable in reorder mode
      toggleComplete(day, run);
    },
    75 // threshold
  );

  // Double-click handlers - disabled in reorder mode
  const doubleClickHandlers = useDoubleClick(
    // Single click - open edit modal (or add modal if no run)
    () => {
      if (isReorderMode) return; // Disable in reorder mode
      if (run) {
        setSelectedDate(day);
        setEditingRun(run);
        setShowAddRun(true);
      } else {
        setSelectedDate(day);
        setShowAddRun(true);
      }
    },
    // Double click - toggle completion (only for existing runs, skip rest days)
    () => {
      if (isReorderMode) return; // Disable in reorder mode
      if (run && !isRest) {
        toggleComplete(day, run);
      }
    },
    250 // delay in ms
  );

  // Long press for drag initiation on mobile
  const [longPressTimer, setLongPressTimer] = React.useState(null);
  const [longPressActive, setLongPressActive] = React.useState(false);

  const handleTouchStart = (e) => {
    if (!isReorderMode || !run) return;

    const timer = setTimeout(() => {
      setLongPressActive(true);
      if (onTouchDragStart) {
        onTouchDragStart(e);
      }
    }, 300); // 300ms long press

    setLongPressTimer(timer);
  };

  const handleTouchEnd = (e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    if (longPressActive) {
      setLongPressActive(false);
      if (onTouchDragEnd) {
        onTouchDragEnd(e);
      }
    }
  };

  const handleTouchCancelInternal = (e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    if (longPressActive) {
      setLongPressActive(false);
    }

    if (onTouchCancel) {
      onTouchCancel(e);
    }
  };

  return (
    <div
      className={verticalLayout ?
        ('rounded-xl p-4 border-2 transition-all min-h-[80px] select-none ' +
          (isReorderMode ? 'cursor-move ' : 'cursor-pointer ') +
          (isDragging ? 'opacity-50 scale-95 ' : '') +
          getBackgroundClass() +
          getBorderClass()) :
        ('rounded-xl p-3 md:p-2 border-2 transition-all transform aspect-square w-full flex items-center justify-center overflow-hidden select-none ' +
          (isReorderMode ? 'cursor-move ' : 'cursor-pointer hover:scale-105 active:scale-95 ') +
          (isDragging ? 'opacity-50 scale-90 ' : '') +
          getBackgroundClass() +
          getBorderClass())}
      style={run && verticalLayout && swipeHandlers.isSwiping && !isReorderMode ? {
        transform: `translateX(${Math.max(-100, Math.min(100, swipeHandlers.swipeOffset))}px)`,
        transition: 'none'
      } : {}}
      {...(!isReorderMode ? doubleClickHandlers : {})}
      onContextMenu={(e) => {
        if (run && !isReorderMode) {
          e.preventDefault();
          setSelectedDate(day);
          setEditingRun(run);
          setShowAddRun(true);
        }
      }}
      {...(run && verticalLayout && !isReorderMode ? swipeHandlers : {})}
      // Drag and drop handlers for desktop
      draggable={isReorderMode && run ? true : false}
      onDragStart={isReorderMode && run && onDragStart ? onDragStart : undefined}
      onDragOver={isReorderMode && onDragOver ? onDragOver : undefined}
      onDragEnter={isReorderMode && onDragEnter ? onDragEnter : undefined}
      onDragLeave={isReorderMode && onDragLeave ? onDragLeave : undefined}
      onDragEnd={isReorderMode && onDragEnd ? onDragEnd : undefined}
      onDrop={isReorderMode && onDrop ? onDrop : undefined}
      // Touch handlers for mobile long-press drag
      onTouchStart={isReorderMode && run ? handleTouchStart : undefined}
      onTouchEnd={isReorderMode ? handleTouchEnd : undefined}
      onTouchCancel={isReorderMode ? handleTouchCancelInternal : undefined}
    >
      {verticalLayout ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showRunEmojis && run && (
              <div className="text-3xl">
                {isRest ? restIconConfig.icon : getRunEmoji(run.type)}
              </div>
            )}
            <div>
              <div className={darkMode ? 'font-bold text-slate-300' : 'font-bold text-slate-700'}>
                {day.toLocaleDateString('en-US', { weekday: 'long' })}
              </div>
              <div className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-500'}>
                {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>
          {run ? (
            isRest ? (
              <div className="flex items-center gap-3">
                <div className={showAsCompleted ? `text-xl font-black ${themeCompletedText}` : 'text-xl font-black'}>
                  Rest
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {run.timeFormat ? (
                  c25kViewMode === 'time' ? (
                    <div className={showAsCompleted ? `text-sm font-semibold ${themeCompletedText} mb-0.5 leading-tight` : 'text-sm font-semibold leading-tight'}>
                      {run.timeFormatLong && (
                        <span className="hidden min-[500px]:inline">{run.timeFormatLong}</span>
                      )}
                      <span className={run.timeFormatLong ? 'inline min-[500px]:hidden' : ''}>{run.timeFormat}</span>
                    </div>
                  ) : (
                    <div className={showAsCompleted ? `text-sm font-semibold ${themeCompletedText} mb-0.5 leading-tight` : 'text-sm font-semibold leading-tight'}>
                      {roundDistance(run.distance)}
                    </div>
                  )
                ) : (
                  run.type !== 'cross' && (
                    <div className={showAsCompleted ? `text-xl font-black ${themeCompletedText} mb-0.5` : 'text-xl font-black'}>
                      {roundDistance(run.distance)}
                    </div>
                  )
                )}
                <div className="text-xs capitalize text-center">
                  <div className={darkMode ? 'text-slate-400' : 'text-slate-600'}>{run.type}</div>
                </div>
              </div>
            )
          ) : (
            <div className={darkMode ? 'text-slate-700 text-2xl font-light' : 'text-slate-300 text-2xl font-light'}>+</div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center">
          {run ? (
            isRest ? (
              <>
                <div className="text-2xl">{restIconConfig.icon}</div>
                <div className={showAsCompleted ? `hidden md:block text-xs ${themeCompletedText} mt-0.5` : (darkMode ? 'hidden md:block text-xs text-slate-400 mt-0.5' : 'hidden md:block text-xs text-slate-600 mt-0.5')}>Rest</div>
              </>
            ) : (
              <>
                {run.timeFormat ? (
                  c25kViewMode === 'time' ? (
                    <div className={showAsCompleted ? `font-semibold text-xs leading-tight text-center ${themeCompletedText}` : 'font-semibold text-xs leading-tight text-center'}>
                      {run.timeFormatLong && (
                        <span className="hidden min-[500px]:inline">{run.timeFormatLong}</span>
                      )}
                      <span className={run.timeFormatLong ? 'inline min-[500px]:hidden' : ''}>{run.timeFormat}</span>
                    </div>
                  ) : (
                    <div className={showAsCompleted ? `font-semibold text-xs leading-tight text-center ${themeCompletedText}` : 'font-semibold text-xs leading-tight text-center'}>
                      {roundDistance(run.distance)}
                      <span className="hidden md:inline text-xs ml-0.5">mi</span>
                    </div>
                  )
                ) : (
                  run.type !== 'cross' && (
                    <>
                      <div className={showAsCompleted ? `font-bold text-base md:text-xl ${themeCompletedText}` : 'font-bold text-base md:text-xl'}>
                        {roundDistance(run.distance)}
                        <span className="hidden md:inline text-xs ml-0.5">mi</span>
                      </div>
                      <div className={darkMode ? 'hidden md:block text-xs text-slate-400 capitalize mt-0.5' : 'hidden md:block text-xs text-slate-500 capitalize mt-0.5'}>{run.type}</div>
                    </>
                  )
                )}
              </>
            )
          ) : (
            <div className={darkMode ? 'text-slate-700 text-2xl md:text-xl font-light' : 'text-slate-300 text-2xl md:text-xl font-light'}>+</div>
          )}
        </div>
      )}
    </div>
  );
};

RunCard.propTypes = {
  day: PropTypes.instanceOf(Date).isRequired,
  run: PropTypes.shape({
    distance: PropTypes.number,
    type: PropTypes.string,
    status: PropTypes.string,
    notes: PropTypes.string,
    timeFormat: PropTypes.string,
    timeFormatLong: PropTypes.string,
    id: PropTypes.number,
  }),
  isCompleted: PropTypes.bool.isRequired,
  isToday: PropTypes.bool.isRequired,
  darkMode: PropTypes.bool.isRequired,
  theme: PropTypes.shape({
    primary: PropTypes.string.isRequired,
    hover: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  verticalLayout: PropTypes.bool.isRequired,
  showRunEmojis: PropTypes.bool.isRequired,
  c25kViewMode: PropTypes.oneOf(['time', 'distance']).isRequired,
  getRunEmoji: PropTypes.func.isRequired,
  toggleComplete: PropTypes.func.isRequired,
  triggerHaptic: PropTypes.func.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  setShowAddRun: PropTypes.func.isRequired,
  setEditingRun: PropTypes.func.isRequired,
  restDayIcon: PropTypes.string.isRequired,
  isReorderMode: PropTypes.bool,
  isDragging: PropTypes.bool,
  isDropTarget: PropTypes.bool,
  onDragStart: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragEnter: PropTypes.func,
  onDragLeave: PropTypes.func,
  onDragEnd: PropTypes.func,
  onDrop: PropTypes.func,
  onTouchDragStart: PropTypes.func,
  onTouchDragEnd: PropTypes.func,
  onTouchCancel: PropTypes.func,
};

export default RunCard;
